import { providerApi } from '@/api/ProviderApi'
import { PENDING_FORMS_QUERY_KEY } from '@/api/hooks/useFetchPendingForms'
import { AVAILABILITY_QUERY_KEY } from '@/api/hooks/usePatientAvailability'
import type { BookVisitRequest, BookedVisit } from '@/api/types/ProviderApi'
import { BOOKING_IN_PROGRESS, SLOT_UNAVAILABLE } from '@/api/types/ProviderApi'
import { isApiRequestError } from '@/errors/ApiRequestError'
import { useMutation, useQueryClient } from '@tanstack/react-query'

/** The chosen time went while the patient was deciding; the picker recovers in place. */
export const isSlotTaken = (error: unknown) =>
	isApiRequestError(error) && error.code === SLOT_UNAVAILABLE

/** The same attempt is already in flight, so waiting is the answer rather than trying again. */
export const isBookingInProgress = (error: unknown) =>
	isApiRequestError(error) && error.code === BOOKING_IN_PROGRESS

const useBookPatientVisit = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (request: BookVisitRequest) => providerApi.bookPatientVisit(request),
		onSuccess: (visit: BookedVisit) => {
			// The visit list and anything keyed under it; a booking can also issue a form.
			queryClient.invalidateQueries({ queryKey: ['upcoming-appointment'] })
			queryClient.invalidateQueries({ queryKey: PENDING_FORMS_QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ['booking-context'] })
			// None of the cached weeks will be shown again, and this booking just took a chair.
			queryClient.removeQueries({ queryKey: [AVAILABILITY_QUERY_KEY] })
			return visit
		},
	})
}

export { useBookPatientVisit }
